/**
 * When to stop trusting an impersonation token.
 *
 * Actor tokens live at most 15 minutes and the platform re-checks `exp` on
 * every request, so a token used right at its edge fails the request that
 * carries it. Both sides therefore treat a token as stale a little early: the
 * frame asks for a fresh one, and the host re-mints before answering, while the
 * old one is still good.
 */

/** How long before `expiresAt` the frame stops using a token. */
export const FRAME_REFRESH_LEEWAY_MS = 60_000;

/**
 * How long before `expiresAt` the host re-mints instead of handing out the
 * current token. Wider than the frame's leeway so a token the host hands out
 * is always fresh by the frame's own measure.
 */
export const HOST_REFRESH_LEEWAY_MS = 120_000;

/**
 * Whether a token expiring at `expiresAt` is still usable at `now`, allowing
 * `leewayMs` of margin. An unparseable `expiresAt` is treated as expired.
 */
export function isTokenFresh(
  expiresAt: string,
  now: number,
  leewayMs: number,
): boolean {
  const expiresAtMs = Date.parse(expiresAt);
  if (Number.isNaN(expiresAtMs)) return false;
  return expiresAtMs - now > leewayMs;
}
